import type { RepwiseEvent } from './events.ts';
import type { WorkoutKind } from './lifts.ts';
import type { Mass } from './mass.ts';
import { floorToLoadable, type Gym } from './plates.ts';

export interface WorkoutRecord {
  kind: WorkoutKind;
  status: 'completed' | 'cancelled' | 'in_progress';
  /** ISO date-time the workout finished (or started, if not finished). */
  at: string;
}

/** Next workout from history, not the day of the week. Cancelled or unfinished workouts don't advance it (US-05). */
export function nextWorkoutKind(history: readonly WorkoutRecord[]): WorkoutKind {
  const done = history.filter((w) => w.status === 'completed').sort((a, b) => a.at.localeCompare(b.at));
  const last = done[done.length - 1];
  if (!last) return 'A';
  return last.kind === 'A' ? 'B' : 'A';
}

/** Return-from-break tiers (ADR-0009): days since last completed workout → suggested reduction. */
export function breakReduction(daysSinceLast: number): number {
  if (daysSinceLast >= 28) return 0.3;
  if (daysSinceLast >= 14) return 0.2;
  if (daysSinceLast >= 7) return 0.1;
  return 0;
}

/**
 * Whole LOCAL calendar days between two instants in an IANA time zone (journey review): Monday 19:00 to next
 * Monday 07:00 is 7 days, not 6; DST changes never shift the count.
 */
export function calendarDaysBetween(fromMs: number, toMs: number, timeZone = 'UTC'): number {
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  const day = (ms: number) => {
    const [y, m, d] = fmt.format(new Date(ms)).split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d) / 86_400_000;
  };
  return day(toMs) - day(fromMs);
}

export function daysBetween(fromIso: string, toIso: string): number {
  const ms = Date.parse(toIso) - Date.parse(fromIso);
  if (Number.isNaN(ms)) throw new RangeError(`Invalid dates: ${fromIso}, ${toIso}`);
  return Math.floor(ms / 86_400_000);
}

/**
 * One reduction, never stacked (ADR-0009 precedence): the larger of a pending deload offer and the
 * break reduction applies. Floors to loadable, never below the bar; a lift at the bar is unchanged.
 */
export function restartWeight(gym: Gym, weight: Mass, breakFraction: number, pendingDeloadFraction = 0): Mass {
  const f = Math.max(breakFraction, pendingDeloadFraction);
  if (f <= 0 || weight <= gym.bar) return weight;
  const to = floorToLoadable(gym, Math.floor(weight * (1 - f))) ?? gym.bar;
  return Math.max(to, gym.bar);
}

/** A local calendar day (YYYY-MM-DD) in a time zone. */
export function localDay(ms: number, timeZone = 'UTC'): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(ms);
}

const addDays = (iso: string, n: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/**
 * J19: when the next workout is. A date the user chose (`next_date_set` after the last finished workout) wins;
 * otherwise the last workout's day plus the plan's rhythm (3 a week → every other day; 2 → every third; 4–5 → the
 * next day), never before today. It only informs: nothing about the programme moves with it.
 */
export function nextWorkoutDate(events: readonly RepwiseEvent[], daysPerWeek: number, now: number, timeZone = 'UTC'): { date: string; planned: boolean } {
  const today = localDay(now, timeZone);
  let lastAt = -1;
  let lastIdx = -1;
  events.forEach((e, i) => {
    if (e.type === 'workout_finished' && e.at > lastAt) (lastAt = e.at), (lastIdx = i);
  });
  for (let i = events.length - 1; i > lastIdx; i--) {
    const e = events[i]!;
    if (e.type === 'next_date_set' && /^\d{4}-\d{2}-\d{2}$/.test(e.date)) return { date: e.date < today ? today : e.date, planned: true };
  }
  if (lastAt < 0) return { date: today, planned: false };
  const gap = Math.max(1, Math.floor(7 / Math.max(1, daysPerWeek)));
  const d = addDays(localDay(lastAt, timeZone), gap);
  return { date: d < today ? today : d, planned: false };
}
